import type { Article } from './articles';

export const articlesStep01Ext: Article[] = [
  {
    id: 60,
    step: '01',
    title: "【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方（初心者向け）",
    category: "知識をつける",
    date: "2026年3月31日",
    readTime: "約15分",
    image: "https://public.readdy.ai/ai/img_res/7f3e3ba990b324e981c3b440873a4a4d.jpg",
    excerpt: "鳥取で家づくりをする人が後から「断熱性能を上げておけばよかった…」と後悔しないために。UA値・C値の基準・断熱材の選び方・住宅会社のチェックポイントまで初心者でもわかるよう体系的に解説します。",
    tags: ["鳥取", "断熱", "気密", "UA値", "C値"],
    content: `
      <h2>はじめに</h2>
      <p>鳥取で家づくりをする人の多くが、あとから<br />「もっと断熱性能を上げておけばよかった…」<br />「冬が寒くて光熱費が高い…」<br />と後悔しています。</p>
      <p>特に鳥取は</p>
      <ul>
        <li>冬の冷え込み</li>
        <li>日本海側の強風</li>
        <li>夏の湿気</li>
      </ul>
      <p>といった気候の影響を強く受けるため、断熱・気密性能は家の快適性を大きく左右する<strong>最重要ポイント</strong>です。</p>
      <p>他の記事でも断熱、気密のことには触れていますが、今回はこの２点にフォーカスして、初心者でも理解できるように、断熱・気密の基礎から、鳥取で後悔しないための基準、住宅会社の選び方までを体系的にまとめました。</p>

      <h2>🔍 この記事でわかること</h2>
      <ul>
        <li>断熱性能（UA値）の基準</li>
        <li>気密性能（C値）の目安</li>
        <li>鳥取の気候に合う断熱レベル</li>
        <li>断熱材の種類と違い</li>
        <li>住宅会社を選ぶときのチェックポイント</li>
      </ul>

      <h2>1. 断熱性能（UA値）とは？｜まずはここを理解する</h2>
      <p>UA値とは、<strong>家全体の熱の逃げやすさを表す数値</strong>です。<br />数字が小さいほど「暖かい家＝高性能」です。</p>
      <h3>■ UA値の目安（全国共通）</h3>
      <table>
        <thead>
          <tr><th>UA値</th><th>断熱等級</th><th>評価</th><th>性能レベル</th></tr>
        </thead>
        <tbody>
          <tr><td>0.87以下</td><td>等級4相当</td><td>省エネ基準</td><td>法律上の最低ライン</td></tr>
          <tr><td>0.60以下</td><td>等級5相当</td><td>ZEH基準</td><td>標準的な高性能住宅</td></tr>
          <tr><td>0.46以下</td><td>等級6相当</td><td>鳥取推奨ライン</td><td>快適性が大きく変わるライン</td></tr>
          <tr><td>0.34以下</td><td>等級6〜7相当</td><td>高断熱住宅</td><td>非常に暖かく光熱費も安い</td></tr>
          <tr><td>0.26以下</td><td>等級7相当</td><td>超高断熱</td><td>北海道・寒冷地レベル</td></tr>
        </tbody>
      </table>

      <h3>■ 鳥取で後悔しないUA値は？</h3>
      <p><strong>結論：UA値0.46以下が理想。</strong></p>
      <p>理由</p>
      <ul>
        <li>冬の冷え込みが強い</li>
        <li>日本海側の風で体感温度が下がる</li>
        <li>光熱費が大きく変わる</li>
      </ul>
      <p>UA値0.6でも「寒い」と感じる人が多く、<strong>0.46前後が最もコスパが良いライン</strong>です。</p>

      <h2>2. 気密性能（C値）とは？｜実は断熱より重要な場合も</h2>
      <p>C値とは、<strong>家の隙間の量を表す数値</strong>です。<br />数字が小さいほど「隙間が少ない＝暖かい家」です。</p>
      <h3>■ C値の目安</h3>
      <table>
        <thead>
          <tr><th>C値</th><th>評価</th><th>状態のイメージ</th></tr>
        </thead>
        <tbody>
          <tr><td>5.0以上</td><td>要注意</td><td>隙間が多く、すきま風が発生</td></tr>
          <tr><td>2.0</td><td>一般的</td><td>旧省エネ基準レベル</td></tr>
          <tr><td>1.0</td><td>現在の目安</td><td>断熱効果が発揮される最低ライン</td></tr>
          <tr><td>0.5以下</td><td>高気密</td><td>換気計画が機能するレベル</td></tr>
          <tr><td>0.1以下</td><td>超高気密</td><td>気密施工の最高水準</td></tr>
        </tbody>
      </table>

      <h3>■ 鳥取で後悔しないC値は？</h3>
      <p><strong>結論：C値1.0以下<br />できれば 0.5以下。</strong></p>
      <p>理由</p>
      <ul>
        <li>冬の風が強く、隙間風が体感温度を下げる</li>
        <li>気密が悪いと断熱材の性能が発揮されない</li>
        <li>光熱費が上がる</li>
      </ul>
      <p>特に鳥取の冬は日本海側からの強風が吹くため、<strong>気密性能が低いと断熱材をいくら入れても暖かくなりません</strong>。</p>

      <h2>3. 断熱材の種類と特徴｜どれを選べばいい？</h2>
      <p>断熱材は大きく3種類に分かれます。</p>

      <h3>■ ① グラスウール（コスパ良い）</h3>
      <ul>
        <li>最も一般的</li>
        <li>価格が安い</li>
        <li>施工品質で性能が左右される</li>
      </ul>
      <p>→ ビルダー・工務店で多い</p>
      <p>⚠️ <strong>施工品質が命です。</strong>正しく施工されないと隙間ができ、断熱性能が大幅に下がります。「高性能グラスウール」を正しく施工できる会社を選ぶことが重要です。</p>

      <h3>■ ② 吹き付け断熱（気密が取りやすい）</h3>
      <ul>
        <li>発泡して隙間を埋める</li>
        <li>気密性が高い</li>
        <li>価格はやや高め</li>
      </ul>
      <p>→ 高性能工務店で採用が多い</p>
      <p>✅ <strong>C値0.5以下を狙いやすい断熱材です。</strong>鳥取の気候には特に相性が良いと言えます。</p>

      <h3>■ ③ 外断熱（高性能）</h3>
      <ul>
        <li>家全体を外側から包む</li>
        <li>断熱性能が高い</li>
        <li>コストが高い</li>
      </ul>
      <p>→ 一部のハウスメーカーで採用</p>

      <h2>4. 鳥取の気候に合う断熱仕様は？</h2>
      <p><strong>結論：「内断熱＋吹き付け断熱」または「高性能グラスウール＋気密施工」が最適。</strong></p>
      <p>理由</p>
      <ul>
        <li>冬の寒さに強い</li>
        <li>コスパが良い</li>
        <li>施工品質が安定しやすい</li>
      </ul>

      <h3>■ 窓も超重要</h3>
      <p>さらに、以下の工夫で体感温度が大きく変わります。</p>
      <table>
        <thead>
          <tr><th>工夫</th><th>効果</th><th>推奨度</th></tr>
        </thead>
        <tbody>
          <tr><td>樹脂サッシ</td><td>窓からの熱損失を大幅軽減</td><td>★★★（鳥取では必須）</td></tr>
          <tr><td>Low-E複層ガラス</td><td>日射取得＋断熱を両立</td><td>★★★</td></tr>
          <tr><td>トリプルガラス</td><td>さらに高い断熱効果</td><td>★★（高性能を目指すなら）</td></tr>
          <tr><td>北側の窓を減らす</td><td>熱損失を減らす</td><td>★★</td></tr>
        </tbody>
      </table>
      <p>⚠️ <strong>アルミサッシは鳥取の冬には不向きです。</strong>アルミは樹脂の約1,000倍熱を通しやすいため、窓まわりが結露・冷気の原因になります。</p>

      <h2>5. 断熱・気密で後悔しないための住宅会社の選び方</h2>

      <h3>■ ① UA値・C値を公開しているか？</h3>
      <p>→ <strong>公開していない会社は要注意。</strong><br />「暖かいです」「高性能です」という曖昧な説明で終わる会社は、数値で語れない可能性があります。</p>

      <h3>■ ② 気密測定を全棟実施しているか？</h3>
      <p>→ <strong>C値は「測定しないと分からない」ため必須。</strong><br />全棟測定を行っている会社は、品質にこだわっている証拠です。</p>

      <h3>■ ③ 標準仕様の窓は何か？</h3>
      <p>→ <strong>アルミ樹脂複合より樹脂サッシが圧倒的に暖かい。</strong><br />窓の性能は断熱に直結します。標準仕様でどちらを採用しているか必ず確認しましょう。</p>

      <h3>■ ④ 断熱材の施工方法は？</h3>
      <p>→ <strong>施工品質が性能を左右する。</strong><br />構造見学会で断熱材の施工状態を確認しましょう。隙間なく施工されているかがポイントです。</p>

      <h3>■ ⑤ 実際に建てた家の冬の体感は？</h3>
      <p>→ <strong>完成見学会で確認すると分かりやすい。</strong><br />冬に見学会に行き、玄関・脱衣所・廊下の寒さを体感してみてください。</p>

      <h2>6. 鳥取でよくある断熱の失敗例</h2>
      <table>
        <thead>
          <tr><th>失敗例</th><th>原因</th><th>対策</th></tr>
        </thead>
        <tbody>
          <tr><td>UA値だけ良くてC値が悪い</td><td>気密を軽視した設計</td><td>C値も必ず確認する</td></tr>
          <tr><td>樹脂サッシではなくアルミ樹脂複合</td><td>標準仕様のコストカット</td><td>窓の種類を事前に確認</td></tr>
          <tr><td>北側に大きな窓をつけてしまう</td><td>採光優先で熱損失を考慮せず</td><td>北側窓は最小限に</td></tr>
          <tr><td>吹き抜けで暖房効率が悪くなる</td><td>吹き抜けの熱計算が甘い</td><td>全館空調とセットで検討</td></tr>
          <tr><td>断熱材の施工ムラ</td><td>施工管理が不十分</td><td>構造見学会で実物確認</td></tr>
        </tbody>
      </table>
      <p>特に<strong>「窓の性能」は体感温度に直結</strong>するため、最優先で確認すべきポイントです。</p>

      <h2>まとめ</h2>
      <p>鳥取で後悔しない断熱・気密性能の基準は次の通りです。</p>
      <table>
        <thead>
          <tr><th>項目</th><th>推奨値</th></tr>
        </thead>
        <tbody>
          <tr><td>UA値（断熱性能）</td><td>0.46以下（できれば0.34以下）</td></tr>
          <tr><td>C値（気密性能）</td><td>1.0以下（できれば0.5以下）</td></tr>
          <tr><td>窓</td><td>樹脂サッシ＋Low-E複層ガラス</td></tr>
          <tr><td>断熱材</td><td>施工品質が命（吹き付け断熱も検討）</td></tr>
          <tr><td>気密測定</td><td>全棟実施が必須</td></tr>
        </tbody>
      </table>
      <p>断熱・気密は「住んでからの快適性」と「光熱費」を大きく左右するため、<strong>住宅会社選びの最重要ポイント</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>この記事でUA値・C値の基準を理解できたら、次のステップとして「断熱等級」の仕組みも押さえておきましょう。<br />等級4〜7の違い・鳥取で選ぶべき基準・夏の暑さ対策まで深掘りしています。</p>
      <p>👉 <a href="/articles/62">「鳥取で後悔しない断熱等級の選び方｜等級4〜7の違いと最適基準を解説」</a>を読むと、住宅会社に「等級6です」と言われたときに正しく判断できるようになります。</p>
      <p>また、断熱・気密と並んで重要なのが「地盤・耐震」です。鳥取は活断層が近く、地盤が弱いエリアも多いため、早めに知っておくべき内容です。</p>
      <p>👉 <a href="/articles/61">「【鳥取版】地盤と耐震で後悔しない家づくり｜地盤調査・耐震等級・地震リスクの基礎知識まとめ」</a>も合わせて読むと、性能面での後悔をほぼゼロにできます。</p>
      <p>さらに、金利が上昇している今の時代に合わせた資金計画も重要です。</p>
      <p>👉 <a href="/articles/63">「鳥取の家づくり総額はどう変わる？金利2.0〜2.5％時代の年収別予算と失敗しない資金計画」</a>を読むと、今の金利で建てても大丈夫かどうかが明確になります。</p>
    `
  },
  {
    id: 61,
    step: '01',
    title: "【鳥取版】地盤と耐震で後悔しない家づくり｜地盤調査・耐震等級・地震リスクの基礎知識まとめ",
    category: "知識をつける",
    date: "2026年3月31日",
    readTime: "約14分",
    image: "https://readdy.ai/api/search-image?query=A%20professional%20geotechnical%20soil%20investigation%20scene%20in%20Japan%2C%20boring%20survey%20equipment%20on%20a%20residential%20plot%20in%20Tottori%20prefecture%2C%20geological%20cross-section%20diagram%20showing%20soil%20layers%2C%20safety%20inspection%20concept%20for%20house%20foundation%2C%20trustworthy%20and%20technical%20atmosphere%2C%20neutral%20beige%20and%20earth%20tones%2C%20no%20people%2C%20clear%20daytime%2C%20mountains%20visible%20in%20background%2C%20Tottori%20Japan%20residential%20land%20site&width=600&height=400&seq=art061&orientation=landscape",
    excerpt: "鳥取は活断層・砂丘地帯・湿地帯が多く、地盤と耐震への注意が特に重要な地域です。地盤調査の仕組み・地盤改良の費用・耐震等級の選び方・住宅会社のチェックポイントまで初心者向けに完全解説します。",
    tags: ["鳥取", "地盤調査", "耐震等級", "地震対策"],
    content: `
      <h2>はじめに</h2>
      <p>家づくりで後悔しやすいポイントのひとつが<strong>「地盤と耐震」</strong>です。</p>
      <p>特に鳥取は、</p>
      <ul>
        <li>地盤が弱いエリアが点在</li>
        <li>近年も大きな地震が発生</li>
        <li>日本海側特有の地形</li>
      </ul>
      <p>といった特徴があり、地盤と耐震性能の重要度が非常に高い地域です。</p>
      <p>この記事では、初心者でも理解できるように、<strong>地盤調査・地盤改良・耐震等級・構造の違い</strong>をわかりやすくまとめました。</p>

      <h2>🔍 この記事でわかること</h2>
      <ul>
        <li>鳥取の地盤の特徴</li>
        <li>地盤調査で何が分かるのか</li>
        <li>地盤改良が必要になるケース</li>
        <li>耐震等級の違い</li>
        <li>木造・鉄骨の耐震性の違い</li>
        <li>住宅会社を選ぶときのチェックポイント</li>
      </ul>

      <h2>1. 鳥取の地盤の特徴｜弱いエリアが多い理由</h2>
      <p>鳥取は、地形と歴史的背景から<strong>地盤が弱い地域が多い</strong>という特徴があります。</p>
      <h3>■ 地盤が弱くなりやすい理由</h3>
      <ul>
        <li>かつての河川跡・湿地帯が多い</li>
        <li>砂丘地帯が広がる（鳥取砂丘だけでなく各地に砂質土壌）</li>
        <li>埋め立て地が点在</li>
        <li>地震の揺れが増幅しやすい地層がある</li>
      </ul>

      <h3>■ 鳥取の地震リスク</h3>
      <table>
        <thead>
          <tr><th>地震名</th><th>規模</th><th>年</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取県西部地震</td><td>M7.3</td><td>2000年</td></tr>
          <tr><td>鳥取県中部地震</td><td>M6.6</td><td>2016年</td></tr>
          <tr><td>島根県東部の地震</td><td>M6.4</td><td>2026年</td></tr>
        </tbody>
      </table>
      <p>特に鳥取市・米子市は、<strong>「地盤が強いエリアと弱いエリアが混在」</strong>しているため、土地選びの段階で注意が必要です。</p>

      <h2>2. 地盤調査とは？｜家を建てる前に必ず行う検査</h2>
      <p>地盤調査は、家を建てる前に<strong>土地の強さを測る検査</strong>です。一般的には「スクリューウエイト貫入試験方法（SWS試験）」が行われます。</p>

      <h3>■ 地盤調査で分かること</h3>
      <ul>
        <li>地盤の固さ</li>
        <li>地層の深さ</li>
        <li>地盤改良が必要かどうか</li>
        <li>建物の沈下リスク</li>
      </ul>

      <h3>■ 調査費用</h3>
      <p>5〜7万円程度（多くは建物費用に含まれる）</p>
      <p>⚠️ <strong>住宅会社が無料で行ってくれるケースが多いですが、必ず実施するよう確認してください。</strong>調査なしで建築を進める会社は信頼性に疑問符がつきます。</p>

      <h2>3. 地盤改良が必要になるケース｜費用はどれくらい？</h2>
      <p>地盤が弱い場合、家が傾くのを防ぐために<strong>地盤改良工事</strong>が必要になります。</p>

      <h3>■ よくある地盤改良の種類</h3>
      <table>
        <thead>
          <tr><th>工法</th><th>特徴</th><th>費用の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>表層改良</td><td>表面から1〜2mの浅い層を固める</td><td>20〜50万円</td></tr>
          <tr><td>柱状改良</td><td>地中にコラム状の杭を作る</td><td>50〜100万円</td></tr>
          <tr><td>鋼管杭</td><td>鋼管を地中深く打ち込む</td><td>80〜150万円</td></tr>
        </tbody>
      </table>

      <h3>■ 鳥取で多いのは？</h3>
      <p>→ <strong>柱状改良が最も多い</strong>（地盤が柔らかい層が深いケースが多いため）</p>

      <h3>■ 注意点</h3>
      <p>地盤改良費は見積もりに含まれていないことが多く、<strong>契約後に追加で80万円かかった…</strong>というケースも珍しくありません。</p>
      <p>👉 <strong>「地盤改良費：別途」と書かれている見積もりは要注意。</strong>事前に想定費用を確認しておきましょう。</p>

      <h2>4. 耐震等級とは？｜地震に強い家の基準</h2>
      <p>耐震等級は、家の「地震に対する強さ」を示す指標です。</p>

      <h3>■ 耐震等級の種類</h3>
      <table>
        <thead>
          <tr><th>耐震等級</th><th>強さの目安</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>等級1</td><td>建築基準法の最低限</td><td>数百年に一度の地震（震度6〜7）で倒壊しないレベル</td></tr>
          <tr><td>等級2</td><td>等級1の1.25倍</td><td>学校・病院など公共施設の基準</td></tr>
          <tr><td>等級3</td><td>等級1の1.5倍</td><td>警察署・消防署など最高レベル。長期優良住宅の条件</td></tr>
        </tbody>
      </table>

      <h3>■ 鳥取で後悔しない耐震等級は？</h3>
      <p><strong>結論：耐震等級3</strong></p>
      <p>理由：</p>
      <ul>
        <li>近年の地震が増加</li>
        <li>地盤が弱い地域が多い</li>
        <li>等級3でもコスト差は10〜30万円程度</li>
        <li>地震保険が割引になる</li>
      </ul>

      <h3>■ 「簡易計算」と「構造計算（許容応力度計算）」の違い</h3>
      <p>耐震等級3でも、どの計算方法を使うかで信頼性が大きく変わります。</p>
      <table>
        <thead>
          <tr><th>計算方法</th><th>特徴</th><th>推奨度</th></tr>
        </thead>
        <tbody>
          <tr><td>壁量計算（簡易）</td><td>法的には問題ないが、地震動の詳細計算なし</td><td>△</td></tr>
          <tr><td>許容応力度計算</td><td>すべての構造部材を詳細に計算。信頼性が高い</td><td>★★★</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>「許容応力度計算で等級3」が最も安心です。</strong></p>

      <h2>5. 木造と鉄骨、どちらが地震に強い？</h2>
      <p><strong>結論：どちらも設計次第で強くできる</strong><br />ただし、特徴は異なります。</p>
      <table>
        <thead>
          <tr><th>項目</th><th>木造（在来工法・2×4）</th><th>鉄骨（軽量・重量）</th></tr>
        </thead>
        <tbody>
          <tr><td>強度</td><td>設計次第で高い</td><td>素材自体の強度が高い</td></tr>
          <tr><td>重量</td><td>軽いので揺れが少ない</td><td>重いが剛性が高い</td></tr>
          <tr><td>間取り自由度</td><td>高い</td><td>大空間が作りやすい</td></tr>
          <tr><td>コスト</td><td>比較的安い</td><td>やや高め</td></tr>
          <tr><td>鳥取での選ばれ方</td><td>多い（コスパが良い）</td><td>ハウスメーカーで選択肢あり</td></tr>
        </tbody>
      </table>
      <p>鳥取では<strong>木造の耐震等級3が最もコスパが良く、選ばれることが多い</strong>です。</p>

      <h2>6. 地盤・耐震で後悔しないための住宅会社の選び方</h2>

      <h3>■ ① 地盤調査の結果を説明してくれるか？</h3>
      <p>→ <strong>調査結果を曖昧にする会社は要注意。</strong><br />「問題ありません」だけで終わる会社より、「この地層だから柱状改良が必要です」と具体的に説明してくれる会社を選びましょう。</p>

      <h3>■ ② 耐震等級3に対応しているか？</h3>
      <p>→ <strong>等級3が標準の会社は信頼性が高い。</strong><br />「等級2も選べます」ではなく、「標準で等級3です」と言える会社が理想です。</p>

      <h3>■ ③ 構造計算（許容応力度計算）をしているか？</h3>
      <p>→ <strong>これがあると耐震性能が確実に担保される。</strong><br />壁量計算だけの会社よりも、許容応力度計算を行う会社のほうが安心感があります。</p>

      <h3>■ ④ 地盤改良費を事前に説明してくれるか？</h3>
      <p>→ <strong>契約後の追加費用を防げる。</strong><br />「地盤改良が必要な場合は50〜100万円が追加になります」と事前に説明できる会社が誠実です。</p>

      <h3>■ ⑤ 実際の施工現場を見られるか？</h3>
      <p>→ <strong>基礎の施工品質が分かる。</strong><br />構造見学会で基礎の配筋・コンクリートの養生状態を確認しましょう。</p>

      <h2>7. 鳥取でよくある地盤・耐震の失敗例</h2>
      <table>
        <thead>
          <tr><th>失敗例</th><th>原因</th><th>対策</th></tr>
        </thead>
        <tbody>
          <tr><td>地盤改良費が追加で100万円かかった</td><td>見積もりに含まれていなかった</td><td>事前に想定費用を確認</td></tr>
          <tr><td>耐震等級を確認せず契約</td><td>営業トークに流された</td><td>等級と計算方法を必ず確認</td></tr>
          <tr><td>地盤が弱いのに平屋を建てて沈下リスク</td><td>地盤調査を軽視</td><td>地盤調査結果を基に改良工事</td></tr>
          <tr><td>基礎の施工が雑で後から補修</td><td>施工管理が甘い</td><td>構造見学会で現場確認</td></tr>
          <tr><td>地震保険の金額が高くなった</td><td>耐震等級が低い</td><td>等級3で保険料が割引になる</td></tr>
        </tbody>
      </table>
      <p>特に<strong>「地盤改良費の見落とし」は非常に多い</strong>ので注意が必要です。</p>

      <h2>まとめ</h2>
      <p>鳥取で後悔しない地盤・耐震性能の基準は次の通りです。</p>
      <table>
        <thead>
          <tr><th>項目</th><th>推奨内容</th></tr>
        </thead>
        <tbody>
          <tr><td>地盤調査</td><td>必ず実施（SWS試験）</td></tr>
          <tr><td>地盤改良費</td><td>50〜100万円を想定しておく</td></tr>
          <tr><td>耐震等級</td><td>等級3が最も安心</td></tr>
          <tr><td>構造計算</td><td>許容応力度計算が必須</td></tr>
          <tr><td>基礎の施工</td><td>構造見学会で必ず確認</td></tr>
        </tbody>
      </table>
      <p>地盤と耐震は、<strong>家の安全性と資産価値を決める最重要ポイント</strong>です。ここを押さえておけば、鳥取の家づくりで大きな失敗を防げます。</p>

      <h2>次に読む記事</h2>
      <p>地盤・耐震の基礎を押さえたら、次は「断熱・気密」の話もぜひ読んでおいてください。鳥取の冬は寒く、性能面で後悔する方が多い分野です。</p>
      <p>👉 <a href="/articles/60">「【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方」</a>を読むと、快適な家づくりへの理解がさらに深まります。</p>
      <p>断熱等級4〜7の違いや、鳥取で選ぶべき等級の基準についてはこちらも合わせてどうぞ。</p>
      <p>👉 <a href="/articles/62">「鳥取で後悔しない断熱等級の選び方｜等級4〜7の違いと最適基準を解説」</a>を読むと、断熱性能への理解がさらに深まります。</p>
      <p>また、金利上昇時代の資金計画についても知っておくと、家づくりの総額イメージが一気に明確になります。</p>
      <p>👉 <a href="/articles/63">「鳥取の家づくり総額はどう変わる？金利2.0〜2.5％時代の年収別予算と失敗しない資金計画」</a>も合わせてご覧ください。</p>
    `
  },
  {
    id: 63,
    step: '01',
    title: "鳥取の家づくり総額はどう変わる？金利2.0〜2.5％時代の年収別予算と失敗しない資金計画",
    category: "知識をつける",
    date: "2026年4月3日",
    readTime: "約16分",
    image: "https://readdy.ai/api/search-image?query=A%20Japanese%20family%20couple%20reviewing%20home%20budget%20documents%20at%20a%20clean%20white%20desk%2C%20calculator%20and%20small%20house%20model%20on%20table%2C%20warm%20natural%20light%2C%20financial%20planning%20concept%20for%20home%20building%20in%20Tottori%2C%20no%20text%20no%20labels%2C%20soft%20beige%20tones%2C%20calm%20and%20organized%20atmosphere%2C%20professional%20lifestyle%20photography%2C%20no%20faces%20visible&width=600&height=400&seq=art063main&orientation=landscape",
    excerpt: "「金利2％台で返済額はどれくらい増えるの？」「鳥取で家を建てるなら総額はいくらが安全？」金利2.0〜2.5％の35年ローンで年収別の安全ライン・鳥取の家づくり総額のリアル・失敗しない資金計画を初心者向けに解説します。",
    tags: ["鳥取", "金利", "資金計画", "年収別予算"],
    content: `
      <h2>① はじめに</h2>
      <p>「金利2％台で返済額はどれくらい増えるの？」「鳥取で家を建てるなら総額はいくらが安全？」<br />最近、鳥取市・米子市・倉吉市で家づくりを検討している20〜30代の方から、私はこの質問を本当に多く受けます。</p>
      <p>私自身も、金利が1％未満だった時代に家づくりを始めたため、金利2％台の返済負担がどれほど変わるのか想像できず不安になった経験があります。</p>
      <p>この記事では、</p>
      <ul>
        <li>金利2.0〜2.5％で返済額がどう変わるか</li>
        <li>年収別の"無理のない予算ライン"</li>
        <li>鳥取の家づくり総額のリアル</li>
        <li>金利が高い時代の失敗しない資金計画</li>
      </ul>
      <p>を初心者向けにわかりやすく解説します。</p>
      <p>読み終える頃には、あなたが今の金利で建てても大丈夫かどうか、そして次に何をすべきかが明確になるはずです。</p>

      <h2>② 結論</h2>
      <p>金利2.0〜2.5％の35年ローンでは、<strong>年収の20〜25％以内の返済額に抑えるのが安全ライン</strong>です。</p>
      <p>そして、鳥取での家づくり総額は<br /><strong>3,000万〜3,800万円が最も一般的な価格帯</strong>ですが、<br />金利が高い今は、<br /><strong>借入額を従来より200〜400万円ほど抑える</strong>必要があります。</p>

      <h2>金利2.0〜2.5％で返済額はどう変わる？【初心者向けに解説】</h2>
      <h3>金利が1％違うと返済額は数百万円変わる</h3>
      <p>金利が上がると、毎月返済額だけでなく総返済額も大きく増えます。</p>
      <p><strong>例：3,000万円を35年ローンで借りた場合</strong></p>
      <table>
        <thead>
          <tr><th>金利</th><th>月々の返済額</th><th>総返済額</th></tr>
        </thead>
        <tbody>
          <tr><td>金利1.0％</td><td>月84,700円</td><td>約3,558万円</td></tr>
          <tr><td>金利2.0％</td><td>月99,400円</td><td>約4,175万円</td></tr>
          <tr><td>金利2.5％</td><td>月107,000円</td><td>約4,494万円</td></tr>
        </tbody>
      </table>
      <p>→ 金利1.0％と2.5％の差：毎月22,300円アップ（年間267,600円）<br />→ 35年で約<strong>936万円の差</strong></p>
      <p>私自身もこの数字を見たとき、「金利ってこんなに影響するのか…」と衝撃を受けました。</p>

      <h3>金利2％台では"返済比率20〜25％"が安全ライン</h3>
      <p>金利が高い時期は、<strong>返済比率（年収に占める返済額の割合）を下げることが最重要</strong>です。</p>
      <table>
        <thead>
          <tr><th>返済比率</th><th>評価</th></tr>
        </thead>
        <tbody>
          <tr><td>返済比率20％</td><td>かなり安全</td></tr>
          <tr><td>返済比率25％</td><td>標準</td></tr>
          <tr><td>返済比率30％</td><td>金利2％台では危険</td></tr>
        </tbody>
      </table>
      <p>金利が低かった時代は30％でも許容されていましたが、<strong>金利2％台では25％以内に抑えることが後悔しない家づくりの鉄則</strong>です。</p>

      <h2>年収別の"無理のない借入額"【金利2.0〜2.5％】</h2>
      <h3>年収別の安全ライン早見表（35年ローン・金利2.5％）</h3>
      <table>
        <thead>
          <tr><th>年収</th><th>返済比率20%の月返済額</th><th>返済比率25%の月返済額</th><th>安全な借入額の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>300万円</td><td>約5万円</td><td>約6.3万円</td><td>1,600〜2,000万円</td></tr>
          <tr><td>350万円</td><td>約5.8万円</td><td>約7.3万円</td><td>1,900〜2,300万円</td></tr>
          <tr><td>400万円</td><td>約6.7万円</td><td>約8.3万円</td><td>2,100〜2,600万円</td></tr>
          <tr><td>450万円</td><td>約7.5万円</td><td>約9.4万円</td><td>2,400〜3,000万円</td></tr>
          <tr><td>500万円</td><td>約8.3万円</td><td>約10.4万円</td><td>2,700〜3,300万円</td></tr>
          <tr><td>600万円</td><td>約10万円</td><td>約12.5万円</td><td>3,200〜4,000万円</td></tr>
        </tbody>
      </table>
      <p>鳥取で最も多い「年収400〜500万円」の層では、<strong>総額2,800〜3,600万円が現実的なライン</strong>です。</p>
      <p>ただし、金利2.5％の今は、従来より<strong>200〜400万円ほど借入額を抑える</strong>ことが安全です。</p>

      <h2>鳥取の家づくり総額はどう変わる？【金利上昇時代の相場】</h2>
      <h3>鳥取のエリア別・総額の目安</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>土地代</th><th>建物代</th><th>外構・諸費用</th><th>総額の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取市</td><td>500〜900万円</td><td>1,800〜2,500万円</td><td>250〜400万円</td><td>2,550〜3,800万円</td></tr>
          <tr><td>米子市</td><td>400〜800万円</td><td>1,800〜2,500万円</td><td>250〜400万円</td><td>2,450〜3,700万円</td></tr>
          <tr><td>倉吉市</td><td>300〜600万円</td><td>1,800〜2,500万円</td><td>250〜400万円</td><td>2,350〜3,500万円</td></tr>
        </tbody>
      </table>
      <p>金利が高い今は、<strong>総額の上限を従来より200〜400万円下げるのが安全</strong>です。</p>
      <p>具体的には、</p>
      <ul>
        <li>建物のグレードを少し落とす（坪単価を5〜10万円下げる）</li>
        <li>土地の広さを少し縮小する</li>
        <li>外構を最低限にして後から追加する</li>
      </ul>
      <p>といった調整が現実的な対策です。</p>
      <p>また、断熱・気密性能は削らないことが重要です。性能を下げると光熱費が上がり、長期的に損をします。<br />👉 <a href="/articles/60">【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方</a></p>

      <h2>金利が高い時代の"失敗しない資金計画"</h2>
      <h3>① 返済額から逆算して予算を決める</h3>
      <p>「借りられる額」ではなく<strong>「返せる額」で考える</strong>のが鉄則です。</p>
      <p>手順：</p>
      <ol>
        <li>今の家賃を確認する</li>
        <li>「家賃＋1〜2万円」が無理のない月返済額</li>
        <li>その返済額から借入可能額を逆算する</li>
        <li>借入額＋頭金＝総予算</li>
      </ol>
      <p>例：家賃6.5万円 → 月返済7.5〜8.5万円 → 借入2,200〜2,600万円が目安（金利2.5%・35年）</p>

      <h3>② 複数の住宅会社で見積もりを比較する</h3>
      <p>金利が高い時期は、<strong>建物価格の差がより致命的</strong>になります。</p>
      <ul>
        <li>同じ35坪でも300〜500万円の差は普通</li>
        <li>その差が月々の返済額に直結する</li>
        <li>最低3社は比較することが必須</li>
      </ul>
      <p>金利が高い今こそ、住宅会社選びで数百万円の差が出ます。</p>

      <h3>③ 見学会で"総額のリアル"を確認する</h3>
      <p>仕様・設備の違いで総額が大きく変わります。</p>
      <ul>
        <li>モデルハウスはオプション仕様が多い</li>
        <li>完成見学会で「この家の総額はいくら？」と必ず聞く</li>
        <li>初心者ほど見学会が必須（ただし、必ず予備知識を持ったうえで参加してください。
その為のお手伝いをするのが本サイトです。）</li>
      </ul>
      <p>👉 <a href="/articles/61">【鳥取版】地盤と耐震で後悔しない家づくり｜地盤調査・耐震等級・地震リスクの基礎知識まとめ</a>も合わせて読むと、見落としがちな費用（地盤改良費）も把握できます。</p>

      <h3>④ 諸費用・外構費を必ず含める</h3>
      <p>見落とすと確実に後悔する費用があります。</p>
      <table>
        <thead>
          <tr><th>項目</th><th>費用の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>外構工事</td><td>80〜150万円</td></tr>
          <tr><td>地盤改良</td><td>0〜100万円</td></tr>
          <tr><td>諸費用（登記・保険・手数料）</td><td>100〜150万円</td></tr>
          <tr><td>カーテン・照明・エアコン</td><td>30〜80万円</td></tr>
        </tbody>
      </table>
      <p>これらを含めると、<strong>建物価格に＋300〜500万円が必要</strong>になります。</p>

      <h2>次に何をすべき？【金利2％台の最短ルート】</h2>
      <h3>① 返済額から"安全ライン"を決める</h3>
      <p>→ 年収の20〜25％以内<br />→ 今の家賃＋1〜2万円が目安</p>

      <h3>② 複数社の見積もりを比較する</h3>
      <p>→ 金利が高い今は特に重要<br />→ 最低3社は比較する</p>

      <h3>③ 見学会で"総額の現実"を知る</h3>
      <p>→ 予算のズレを防ぐ<br />→ 完成見学会で「総額はいくら？」を必ず確認</p>

      <h3>④ 資料請求で仕様・価格を比較</h3>
      <p>→ 失敗しない家づくりの最短ルート<br />→ 複数社の標準仕様を比較することで、コスパの良い会社が見えてくる</p>

      <h2>まとめ</h2>
      <ul>
        <li>金利2.0〜2.5％では返済負担が大きく増える</li>
        <li>年収の20〜25％以内の返済額が安全</li>
        <li>鳥取の家づくり総額は3,000〜3,800万円が一般的</li>
        <li>金利が高い今は借入額を200〜400万円抑えるのが現実的</li>
        <li>住宅会社によって総額は300〜500万円変わる</li>
        <li>見学会・資料請求で比較することが失敗しないコツ</li>
      </ul>
      <p>金利が高い時代でも、正しい知識があれば家づくりは十分可能です。今日の段階で、あなたはすでに一歩リードしています。</p>

      <h2>次に読む記事</h2>
      <p>金利が高い時期は、建物性能（断熱・耐震）によって総額が変わることも重要です。性能を理解しておくと、見積もり比較が一気にラクになります。</p>
      <ul>
        <li>👉 <a href="/articles/60">【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方（初心者向け）</a></li>
        <li>👉 <a href="/articles/61">【鳥取版】地盤と耐震で後悔しない家づくり｜地盤調査・耐震等級・地震リスクの基礎知識まとめ</a></li>
        <li>👉 <a href="/articles/19">鳥取の家づくり予算はいくら必要？年収別の目安と失敗しない決め方</a></li>
      </ul>
    `
  },
  {
    id: 64,
    step: '01',
    title: "鳥取の地盤は弱い？家づくりで選ぶべき基礎の種類と後悔しない選び方【基礎断熱まで解説】",
    category: "知識をつける",
    date: "2026年4月5日",
    readTime: "約15分",
    image: "https://readdy.ai/api/search-image?query=A%20skilled%20Japanese%20construction%20worker%20wearing%20safety%20helmet%20and%20work%20gloves%20carefully%20arranging%20steel%20rebar%20grid%20on%20a%20concrete%20slab%20foundation%20at%20a%20residential%20construction%20site%20in%20Japan%2C%20realistic%20photo%20style%2C%20natural%20daylight%2C%20concrete%20formwork%20visible%20around%20the%20perimeter%2C%20sandy%20soil%20visible%20in%20background%2C%20authentic%20construction%20site%20atmosphere%2C%20ground%20level%20perspective%20showing%20the%20rebar%20lattice%20pattern%2C%20professional%20craftsman%20at%20work%20on%20house%20foundation&width=600&height=400&seq=art064worker02&orientation=landscape",
    excerpt: "「鳥取って地盤が弱いって本当？」「ベタ基礎と布基礎どっちがいい？」「基礎断熱って必要？」鳥取の地盤特性・基礎の種類・基礎断熱のメリットデメリット・後悔しない選び方を初心者向けに完全解説します。",
    tags: ["鳥取", "地盤", "ベタ基礎", "布基礎", "基礎断熱"],
    content: `
      <h2>① はじめに</h2>
      <p>「鳥取って地盤が弱いって聞くけど本当？」<br />「ベタ基礎と布基礎ってどっちがいいの？」<br />「基礎断熱って必要？シロアリが心配…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを検討している20〜30代の方から、私はこの質問を本当に多く受けます。</p>
      <p>私自身も家づくりの初期は、地盤の強さや基礎の違いが全くわからず、住宅会社の説明が呪文のように聞こえた経験があります。</p>
      <p>この記事では、</p>
      <ul>
        <li>鳥取の地盤の特徴</li>
        <li>選ぶべき基礎の種類</li>
        <li>基礎断熱のメリット・デメリット</li>
        <li>地盤 × 基礎 × 断熱の最適な組み合わせ</li>
        <li>後悔しない基礎の選び方</li>
      </ul>
      <p>を初心者向けにわかりやすく解説します。</p>
      <p>読み終える頃には、あなたの土地に最適な基礎が明確になり、住宅会社選びの判断軸が一気にクリアになるはずです。</p>

      <h2>② 結論</h2>
      <p>結論として、<strong>鳥取の家づくりでは「ベタ基礎＋基礎内断熱」が最も最適で後悔の少ない選択肢</strong>です。</p>
      <p>理由は以下の通りです。</p>
      <ul>
        <li>鳥取は軟弱地盤が多く、ベタ基礎の安定性が有利</li>
        <li>冬の冷え込みが強く、基礎断熱の暖かさが大きなメリット</li>
        <li>高気密高断熱住宅との相性が良い</li>
        <li>床下エアコンなど最新の省エネ設備が使える</li>
        <li>施工実績のある住宅会社が増えてきた</li>
      </ul>
      <p>もちろん、施工品質やシロアリ対策は必須ですが、<strong>総合的に見て鳥取の気候・地盤・住宅性能のトレンドに最も合うのが「ベタ基礎＋基礎断熱」</strong>です。</p>

      <h2>鳥取の地盤は弱い？エリア別の特徴</h2>
      <h3>鳥取は「強い地盤」と「弱い地盤」が混在している</h3>
      <p>鳥取県は地形が複雑で、地盤が強いエリアと弱いエリアが極端に分かれています。</p>
      <ul>
        <li><strong>鳥取市：</strong>砂丘地帯・沖積層が多く柔らかい地盤が点在</li>
        <li><strong>米子市：</strong>中海周辺は軟弱地盤が多い</li>
        <li><strong>倉吉市：</strong>比較的安定した地盤が多いが場所による</li>
        <li><strong>湯梨浜・琴浦：</strong>海沿いは軟弱地盤が多い傾向</li>
      </ul>
      <p>私自身も鳥取市で家づくりをした際、地盤調査で「改良が必要」と言われて追加費用が発生した経験があります。</p>

      <h3>地盤が弱いとどうなる？</h3>
      <ul>
        <li>建物が不同沈下するリスク</li>
        <li>基礎に負担がかかる</li>
        <li>地震時の揺れが大きくなる</li>
        <li>地盤改良費が高くなる</li>
      </ul>
      <p>だからこそ、<strong>地盤に合った基礎選びが超重要</strong>です。</p>

      <h2>基礎の種類は2つだけ【ベタ基礎・布基礎】</h2>
      <h3>① ベタ基礎（鳥取では最適）</h3>
      <p><strong>特徴</strong></p>
      <ul>
        <li>床下全体をコンクリートで覆う</li>
        <li>耐震性が高い</li>
        <li>地盤が弱い土地でも採用しやすい</li>
        <li>シロアリ対策にも強い</li>
        <li>基礎断熱との相性が良い</li>
      </ul>
      <p>鳥取の地盤事情を考えると、<strong>ベタ基礎が最も安定し、後悔が少ない選択肢</strong>です。</p>

      <h3>② 布基礎（コスト重視）</h3>
      <p><strong>特徴</strong></p>
      <ul>
        <li>壁の下だけにコンクリートを配置</li>
        <li>コストは安い</li>
        <li>地盤が強い土地向け</li>
        <li>地震に弱いわけではないが、ベタ基礎よりは劣る</li>
      </ul>
      <p>今回は布基礎の説明も行いましたが、現在の工務店ではベタ基礎がほとんど標準となっています。</p>

      <h2>基礎断熱とは？鳥取で採用すべき理由</h2>
      <h3>基礎断熱とは？</h3>
      <p>基礎の立ち上がり部分に断熱材を貼り、<strong>床下を室内と同じ"断熱空間"として扱う工法</strong>です。</p>

      <h3>鳥取で基礎断熱が最適な理由</h3>
      <p><strong>① 冬の冷え込みが強い</strong><br />→ 床下が暖かくなり、体感温度が大きく改善</p>
      <p><strong>② 高気密高断熱住宅が主流になってきた</strong><br />→ UA値0.46以下（G2）を目指すなら相性が良い</p>
      <p><strong>③ 床下エアコンが使える</strong><br />→ ランニングコストが安く、鳥取の冬に強い</p>
      <p><strong>④ ベタ基礎との相性が抜群</strong><br />→ 気密が取りやすく、施工品質が安定しやすい</p>

      <h3>デメリット（ここは正直に）</h3>
      <ul>
        <li>シロアリリスクが上がる</li>
        <li>施工品質の差が出やすい</li>
        <li>コストが上がる</li>
        <li>メンテナンスが必要</li>
      </ul>
      <p>ただし、最近はシロアリ対策の技術が進化し、<strong>施工実績のある会社ならリスクは大幅に低減</strong>できます。</p>

      <h2>鳥取で最適な組み合わせは「ベタ基礎＋基礎断熱」</h2>
      <h3>鳥取の地盤 × 気候 × 性能の相性が最強</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>ベタ基礎＋基礎断熱</th><th>布基礎＋床断熱</th></tr>
        </thead>
        <tbody>
          <tr><td>軟弱地盤への対応</td><td>◎ 安定性が高い</td><td>△ 地盤が強い土地向け</td></tr>
          <tr><td>冬の暖かさ</td><td>◎ 床下が暖かい</td><td>△ 床下が冷える</td></tr>
          <tr><td>気密性能</td><td>◎ 取りやすい</td><td>△ 取りにくい</td></tr>
          <tr><td>床下エアコン</td><td>◎ 使える</td><td>✕ 使えない</td></tr>
          <tr><td>シロアリリスク</td><td>△ 対策が必要</td><td>◎ リスクが低い</td></tr>
          <tr><td>コスト</td><td>△ やや高め</td><td>◎ 安い</td></tr>
        </tbody>
      </table>
      <p>総合的に見て、<strong>鳥取では「ベタ基礎＋基礎断熱」が最も合理的で後悔の少ない選択肢</strong>です。</p>

      <h2>後悔しない基礎の選び方</h2>
      <h3>① 地盤調査の結果を必ず確認</h3>
      <p>→ SWS試験のデータを見せてもらう<br />→ 地盤改良が必要かどうかを事前に把握する</p>

      <h3>② 住宅会社の基礎仕様を比較</h3>
      <ul>
        <li>ベタ基礎の厚み</li>
        <li>鉄筋のピッチ</li>
        <li>防湿シート</li>
        <li>シロアリ対策</li>
      </ul>

      <h3>③ 基礎断熱の施工実績を確認</h3>
      <p>→ 写真・保証・断熱材の種類を必ず確認する</p>

      <h3>④ 見学会で基礎をチェック</h3>
      <p>→ <strong>構造見学会が最重要</strong><br />→ 実際の基礎の施工状態を目で確認することが一番の安心材料</p>

      <h2>まとめ</h2>
      <ul>
        <li>鳥取は軟弱地盤が多く、基礎選びが超重要</li>
        <li>ベタ基礎は安定性・耐震性が高く鳥取向き</li>
        <li>基礎断熱は鳥取の寒さに強く、快適性が大幅UP</li>
        <li>「ベタ基礎＋基礎断熱」が最も後悔の少ない選択肢</li>
        <li>施工品質とシロアリ対策は必ず確認</li>
        <li>見学会・資料請求で比較することが失敗しないコツ</li>
      </ul>
      <p>基礎は<strong>"家の寿命を決める最重要ポイント"</strong>です。今日の段階で、あなたはすでに一歩リードしています。</p>

      <h2>次に読む記事</h2>
      <p>基礎を理解すると、断熱性能・耐震性能との関係も見えてきます。</p>
      <p>基礎断熱についてもう少し詳しく知りたい方は、<br />👉 <a href="/articles/65">「鳥取で基礎断熱は本当に必要？基礎外断熱・基礎内断熱の違いと後悔しない選び方【完全ガイド】」</a><br />を読むと一層理解が深まります。</p>
      <p>断熱・気密について基本的なことが知りたい方は、<br />👉 <a href="/articles/60">「【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方（初心者向け）」</a><br />を読むと色々わかってくると思います。</p>
    `
  },
  {
    id: 65,
    step: '01',
    title: "鳥取で基礎断熱は本当に必要？基礎外断熱・基礎内断熱の違いと後悔しない選び方【完全ガイド】",
    category: "知識をつける",
    date: "2026年4月5日",
    readTime: "約16分",
    image: "https://readdy.ai/api/search-image?query=A%20Japanese%20construction%20worker%20installing%20rigid%20foam%20insulation%20boards%20on%20the%20interior%20side%20of%20a%20concrete%20foundation%20wall%20at%20a%20residential%20building%20site%2C%20realistic%20photo%20style%2C%20natural%20daylight%20inside%20the%20foundation%20formwork%2C%20white%20EPS%20insulation%20panels%20being%20attached%20to%20gray%20concrete%20foundation%20walls%2C%20authentic%20construction%20site%20atmosphere%2C%20close-up%20view%20showing%20insulation%20installation%20detail%2C%20professional%20craftsman%20working%20on%20foundation%20thermal%20insulation%20in%20Japan&width=600&height=400&seq=art065insulation02&orientation=landscape",
    excerpt: "「基礎断熱って暖かいけどシロアリが怖い…」「基礎外断熱と基礎内断熱って何が違うの？」鳥取の家づくりで最適な断熱方法・シロアリ対策・床断熱との比較・後悔しない選び方を初心者向けに完全解説します。",
    tags: ["鳥取", "基礎断熱", "基礎外断熱", "基礎内断熱", "シロアリ対策"],
    content: `
      <h2>① はじめに</h2>
      <p>「基礎断熱って暖かいって聞くけど、シロアリが怖い…」<br />「基礎外断熱と基礎内断熱って何が違うの？」<br />「鳥取の家づくりではどっちを選べばいいの？」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを検討している20〜30代の方から、私はこの質問を本当に多く受けます。</p>
      <p>私自身も家づくりの初期は、基礎断熱の仕組みやリスクが全くわからず、住宅会社によって言うことが違うため混乱した経験があります。</p>
      <p>この記事では、</p>
      <ul>
        <li>基礎断熱の仕組み</li>
        <li>基礎外断熱・基礎内断熱の違い</li>
        <li>鳥取で採用すべき断熱方法</li>
        <li>メリット・デメリット</li>
        <li>シロアリ対策</li>
        <li>床断熱との比較</li>
        <li>後悔しない選び方</li>
      </ul>
      <p>を初心者向けにわかりやすく解説します。</p>
      <p>読み終える頃には、あなたの家に最適な断熱方法が明確になり、住宅会社選びの判断軸が一気にクリアになるはずです。</p>

      <h2>② 結論</h2>
      <p>結論として、<strong>鳥取の家づくりでは「ベタ基礎＋基礎内断熱」が最も最適で後悔の少ない選択肢</strong>です。</p>
      <p>理由は以下の通りです。</p>
      <ul>
        <li>鳥取の冬は寒く、基礎断熱の暖かさが大きなメリット</li>
        <li>基礎外断熱はシロアリリスクが高く、鳥取では不向き</li>
        <li>基礎内断熱はベタ基礎との相性が良く、気密が安定</li>
        <li>床下エアコンなど最新の暖房方式が使える</li>
        <li>高気密高断熱住宅（G2以上）との相性が抜群</li>
      </ul>
      <p>つまり、<strong>鳥取の気候 × 地盤 × 性能住宅のトレンドを考えると「ベタ基礎＋基礎内断熱」が最適解</strong>です。</p>

      <h2>基礎断熱とは？初心者向けにわかりやすく解説</h2>
      <h3>基礎断熱の仕組み</h3>
      <p>基礎断熱とは、基礎の立ち上がり部分に断熱材を施工し、<strong>床下を"室内扱い"にする工法</strong>です。</p>
      <ul>
        <li>床下の温度が安定</li>
        <li>気密が取りやすい</li>
        <li>床下エアコンが使える</li>
      </ul>
      <p>という特徴があります。</p>

      <h3>床断熱との違い</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>基礎断熱</th><th>床断熱</th></tr>
        </thead>
        <tbody>
          <tr><td>断熱の位置</td><td>基礎の立ち上がり部分</td><td>床（フローリングの下）</td></tr>
          <tr><td>床下の扱い</td><td>室内扱い（暖かい）</td><td>外気扱い（冷える）</td></tr>
          <tr><td>気密性</td><td>取りやすい</td><td>取りにくい</td></tr>
          <tr><td>床下エアコン</td><td>使える</td><td>使えない</td></tr>
          <tr><td>シロアリリスク</td><td>やや高い</td><td>低い</td></tr>
          <tr><td>コスト</td><td>やや高め</td><td>安い</td></tr>
        </tbody>
      </table>
      <p>鳥取の冬の寒さを考えると、<strong>暖かさ・快適性では基礎断熱が圧勝</strong>です。</p>

      <h2>基礎外断熱と基礎内断熱の違い</h2>
      <h3>① 基礎外断熱（外側に断熱材を貼る）</h3>
      <p><strong>特徴</strong></p>
      <ul>
        <li>基礎の外側に断熱材を施工</li>
        <li>断熱性能は高い</li>
        <li>施工が簡単</li>
      </ul>
      <p><strong>デメリット（鳥取では致命的）</strong></p>
      <ul>
        <li>シロアリリスクが非常に高い</li>
        <li>断熱材が外気に触れるため劣化しやすい</li>
        <li>鳥取の湿気・雨・積雪と相性が悪い</li>
      </ul>
      <p>→ 鳥取ではほぼ採用されない<br />→ 住宅会社も避ける傾向</p>

      <h3>② 基礎内断熱（内側に断熱材を貼る）</h3>
      <p><strong>特徴</strong></p>
      <ul>
        <li>基礎の内側に断熱材を施工</li>
        <li>断熱材が外気に触れない</li>
        <li>ベタ基礎との相性が良い</li>
        <li>気密が取りやすい</li>
      </ul>
      <p><strong>メリット</strong></p>
      <ul>
        <li>シロアリリスクが低い</li>
        <li>断熱材が劣化しにくい</li>
        <li>床下エアコンと相性抜群</li>
      </ul>
      <p>→ <strong>鳥取では「基礎内断熱」が最適解</strong></p>

      <h2>鳥取で基礎内断熱が最適な理由</h2>
      <h3>① 冬の冷え込みが強い地域だから</h3>
      <p>鳥取の冬は</p>
      <ul>
        <li>最低気温0℃前後</li>
        <li>風が強く体感温度が低い</li>
        <li>床下からの冷気が強い</li>
      </ul>
      <p>基礎内断熱なら、<strong>床下の温度が室内とほぼ同じになり、底冷えがなくなる</strong>。</p>

      <h3>② ベタ基礎との相性が抜群</h3>
      <p>鳥取は軟弱地盤が多く、ベタ基礎が標準になりつつあります。ベタ基礎は</p>
      <ul>
        <li>床下がコンクリートで密閉</li>
        <li>気密が取りやすい</li>
        <li>シロアリ侵入経路が限定される</li>
      </ul>
      <p>→ <strong>基礎内断熱と組み合わせると最強</strong></p>

      <h3>③ 高気密高断熱住宅（G2以上）が主流に</h3>
      <p>鳥取でも</p>
      <ul>
        <li>UA値0.46（G2）</li>
        <li>C値0.5以下</li>
      </ul>
      <p>を目指す住宅会社が増えています。基礎内断熱は<strong>気密が安定しやすく、性能住宅との相性が良い</strong>。</p>

      <h3>④ 床下エアコンが使える</h3>
      <p>鳥取の冬に最適な暖房方式として人気なのが床下エアコン。基礎内断熱でないと採用できません。</p>
      <ul>
        <li>家全体が均一に暖まる</li>
        <li>ランニングコストが安い</li>
        <li>足元が暖かい</li>
      </ul>
      <p>→ <strong>鳥取の冬に最適</strong></p>

      <h2>基礎断熱のメリット・デメリット</h2>
      <h3>メリット</h3>
      <ul>
        <li>床下が暖かく快適</li>
        <li>気密が取りやすい</li>
        <li>床下エアコンが使える</li>
        <li>冬の底冷えがなくなる</li>
        <li>断熱性能が安定</li>
        <li>メンテナンス性が高い（床下に入れる）</li>
      </ul>

      <h3>デメリット（正直に）</h3>
      <ul>
        <li>シロアリリスクが上がる</li>
        <li>施工品質の差が出やすい</li>
        <li>コストが上がる</li>
        <li>断熱材の種類で性能差が大きい</li>
      </ul>
      <p>ただし、<strong>正しいシロアリ対策＋施工実績のある会社を選べばリスクは大幅に低減</strong>できます。</p>

      <h2>基礎断熱のシロアリ対策【ここを知らないと危険】</h2>
      <h3>① 防蟻断熱材を使う</h3>
      <p>→ EPS＋防蟻剤入りが一般的<br />→ 鳥取では必須レベル</p>

      <h3>② 基礎外断熱は避ける</h3>
      <p>→ 外側に断熱材を貼るとシロアリリスクが高い<br />→ 鳥取では「基礎内断熱」が基本</p>

      <h3>③ 基礎と土台の隙間を塞ぐ</h3>
      <p>→ 気密パッキン<br />→ シロアリ侵入経路を遮断</p>

      <h3>④ 施工会社の実績が最重要</h3>
      <p>→ 基礎断熱は"経験値"がすべて<br />→ 施工写真・保証内容を必ず確認</p>

      <h2>後悔しない基礎断熱の選び方</h2>
      <h3>① 断熱材の種類を確認</h3>
      <p>→ EPS防蟻材が最も安心</p>

      <h3>② 施工実績を必ず確認</h3>
      <p>→ 写真・保証・施工年数</p>

      <h3>③ ベタ基礎との組み合わせを選ぶ</h3>
      <p>→ 鳥取では必須レベル</p>

      <h3>④ 床下エアコンを検討する</h3>
      <p>→ 基礎断熱のメリットを最大化</p>

      <h3>⑤ 構造見学会で基礎をチェック</h3>
      <p>→ 施工品質が一目でわかる</p>

      <h2>まとめ</h2>
      <ul>
        <li>鳥取の冬は寒く、基礎断熱の暖かさが大きなメリット</li>
        <li>基礎外断熱はシロアリリスクが高く鳥取では不向き</li>
        <li>基礎内断熱はベタ基礎との相性が抜群</li>
        <li>床下エアコンが使える</li>
        <li>高気密高断熱住宅（G2以上）との相性が良い</li>
        <li>施工品質とシロアリ対策が最重要</li>
      </ul>
      <p>総合的に見て、<strong>鳥取では「ベタ基礎＋基礎内断熱」が最も快適で後悔の少ない選択肢</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>基礎断熱を理解すると、断熱等級・耐震等級・基礎の種類との関係も見えてきます。</p>
      <p>👉 <a href="/articles/61">「【鳥取版】地盤と耐震で後悔しない家づくり｜地盤調査・耐震等級・地震リスクの基礎知識まとめ」</a></p>
      <p>👉 <a href="/articles/62">「鳥取で後悔しない断熱等級の選び方｜等級4〜7の違いと最適基準を解説」</a></p>
    `
  },
  {
    id: 62,
    step: '01',
    title: "鳥取で後悔しない断熱等級の選び方｜等級4〜7の違いと最適基準を解説",
    category: "知識をつける",
    date: "2026年3月31日",
    readTime: "約15分",
    image: "https://public.readdy.ai/ai/img_res/edited_dd1cfec63f21cc8da89f141d7334c37d_f66f8c4a.jpg",
    excerpt: "「断熱等級って何？」「等級6と7はどう違うの？」鳥取で家づくりをするなら断熱等級6以上が最低ライン。冬の寒さだけでなく夏の猛暑対策・光熱費の観点から、等級4〜7の違いと鳥取に最適な基準を初心者向けに解説します。",
    tags: ["鳥取", "断熱等級", "等級6", "等級7", "夏の暑さ"],
    content: `
      <h2>① はじめに｜断熱等級って何？</h2>
      <p>「断熱等級って何？」「等級6と7ってどう違うの？」<br />鳥取で家づくりを始めると必ず耳にする言葉ですが、正直よく分からないまま話が進んでしまう人が多いです。</p>
      <p>しかも最近は、<strong>"冬の寒さ" だけでなく "夏の暑さ" に悩む人が急増</strong>しています。</p>
      <p>私自身も、見学会で<br />「冬は暖かいけど、夏はどうなんだろう…？」<br />と疑問に思った経験があります。</p>
      <p>前回の記事<br />『<a href="/articles/60">【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方</a>』<br />では、断熱性能の"中身"を解説しましたが、今回はさらに深掘りして<br /><strong>断熱等級そのものの意味・違い・選び方</strong><br />を初心者向けに分かりやすく解説します。</p>
      <p>この記事を読むことで、<br />「冬も夏も快適な家を建てるために、どの等級を選べばいいか」<br />が明確になります。</p>

      <h2>② 結論｜鳥取で選ぶべき断熱等級は？</h2>
      <p>鳥取で家を建てるなら<strong>断熱等級6以上が"最低ライン"</strong>です。</p>
      <p>理由は、</p>
      <ul>
        <li>冬の寒さ</li>
        <li>夏の猛暑</li>
        <li>光熱費の高騰</li>
      </ul>
      <p>のすべてに対応できるバランスの良い基準だからです。</p>

      <h2>断熱等級とは？初心者向けにわかりやすく解説</h2>
      <p>断熱等級とは、家の「断熱性能」を国が定めた基準で、<strong>数字が大きいほど"冬暖かく・夏涼しい家"</strong>になります。</p>
      <h3>断熱等級4〜7の違い（ざっくり理解）</h3>
      <table>
        <thead>
          <tr><th>断熱等級</th><th>UA値の目安（6地域）</th><th>特徴</th><th>評価</th></tr>
        </thead>
        <tbody>
          <tr><td>等級4</td><td>0.87以下</td><td>昔の基準。冬も夏も不十分</td><td>△ 最低限</td></tr>
          <tr><td>等級5</td><td>0.60以下</td><td>ZEHレベル。一定の省エネ性能</td><td>○ 標準</td></tr>
          <tr><td>等級6</td><td>0.46以下</td><td>高断熱。冬も夏も快適</td><td>◎ 推奨</td></tr>
          <tr><td>等級7</td><td>0.26以下</td><td>国内最高レベル。北海道並みの断熱性</td><td>★ 最高</td></tr>
        </tbody>
      </table>
      <p>前回の記事でまとめたUA値との対応も参考にしてください。<br />👉 <a href="/articles/60">【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方</a></p>

      <h2>鳥取で断熱等級6以上が必須な理由（冬＋夏の両面）</h2>
      <h3>① 冬の寒さ対策としての断熱等級</h3>
      <p>鳥取市・米子市・倉吉市は冬の最低気温が0℃前後まで下がり、湿度も高いため体感温度はさらに低くなります。</p>
      <p>等級4〜5の家だと</p>
      <ul>
        <li>足元が冷える</li>
        <li>朝の室温が10℃以下</li>
        <li>結露が発生しやすい</li>
      </ul>
      <p>という後悔が多いです。</p>

      <h3>② 夏の猛暑対策としての断熱等級（重要ポイント）</h3>
      <p>ここ数年、鳥取でも<strong>35℃を超える猛暑日が当たり前</strong>になっています。断熱等級が低い家は、</p>
      <ul>
        <li>外壁から熱が侵入</li>
        <li>屋根裏が60℃以上に</li>
        <li>エアコンが効かない</li>
      </ul>
      <p>という"夏の後悔"が非常に多いです。</p>
      <p>私自身も、等級4の家を見学したとき「エアコンつけてるのに2階が暑い…」と感じたことがあります。</p>
      <p>断熱等級6以上なら</p>
      <ul>
        <li>外からの熱を遮断</li>
        <li>室内温度が上がりにくい</li>
        <li>エアコン1台で家全体が快適</li>
      </ul>
      <p>というメリットがあり、夏の快適性が大きく変わります。</p>

      <h3>③ 光熱費が"冬も夏も"大きく変わる</h3>
      <p>断熱等級6と等級4では、<strong>年間の冷暖房費が3〜5万円変わる</strong>と言われています。冬だけでなく、夏の冷房費も大きく下がるため、長期的に見て断熱等級6以上は"元が取れる性能"です。</p>

      <h2>断熱等級で失敗しない住宅会社の選び方</h2>
      <h3>① 等級の"根拠"を必ず確認する</h3>
      <p>「うちは等級6です」と言うだけなら誰でもできます。大事なのは</p>
      <ul>
        <li>計算書</li>
        <li>設計図</li>
        <li>証明書</li>
      </ul>
      <p>を提示できるかどうか。</p>

      <h3>② 鳥取の気候に合わせた提案ができるか</h3>
      <p>鳥取市・米子市・倉吉市は気候が微妙に違います。地域ごとの断熱提案ができる会社は信頼できます。</p>

      <h3>③ 複数社を比較して"標準仕様"を見極める</h3>
      <p>同じ価格帯でも</p>
      <ul>
        <li>等級5が標準の会社</li>
        <li>等級6が標準の会社</li>
      </ul>
      <p>では住み心地が大きく変わります。</p>
      <p>👉 資料請求で標準仕様を比較すると、違いが一目で分かります。</p>

      <h2>見学会でチェックすべきポイント（実体験ベース）</h2>
      <h3>① 夏の涼しさ（ここが重要）</h3>
      <ul>
        <li>2階が暑くないか</li>
        <li>エアコン1台でどこまで涼しいか</li>
        <li>日射遮蔽（窓の遮熱性能）が十分か</li>
      </ul>
      <p>夏の快適性は断熱等級の差が最も出る部分です。</p>

      <h3>② 冬の暖かさ</h3>
      <p>エアコン1台で家全体が暖かいかを確認。玄関・脱衣所・廊下の温度差があるかどうかも大切なチェックポイントです。</p>

      <h3>③ 断熱材の施工精度</h3>
      <p>断熱材が隙間なく施工されているかは重要です。構造見学会で実際の断熱材を確認しましょう。</p>

      <h2>まとめ</h2>
      <p>鳥取で後悔しない家づくりをするためには</p>
      <ul>
        <li>断熱等級6以上</li>
        <li>夏の暑さ対策としても断熱性能を重視</li>
        <li>UA値・C値もセットで確認</li>
      </ul>
      <p>が必須です。</p>
      <p>断熱性能は「冬の快適さ」だけでなく、<strong>夏の涼しさ・光熱費・健康</strong>にも直結します。</p>

      <h2>次に読む記事</h2>
      <p>断熱性能の"中身"（UA値・C値・断熱材の種類）をもっと理解したい方は<br />👉 <a href="/articles/60">「【鳥取版】断熱・気密で後悔しない家づくり｜UA値・C値の基準と最適な選び方」</a><br />も合わせて読むと理解が深まります。</p>
      <p>また、断熱・気密と並んで重要なのが「地盤・耐震」です。鳥取は活断層が近く、地盤が弱いエリアも多いため、安全な家づくりの基礎知識として知っておきましょう。</p>
      <p>👉 <a href="/articles/61">「【鳥取版】地盤と耐震で後悔しない家づくり｜地盤調査・耐震等級・地震リスクの基礎知識まとめ」</a>を読むと、安全な家を建てるための知識が身につきます。</p>
    `
  }
];
